import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { store } from '@/ui/store';
import { useStore } from '@/ui/useStore';
import { suggestAt, type Suggestion } from '@/engine/suggest';
import { colourRefs } from '@/model/address';
import { Popover, rectOf } from '@/ui/Popover';

interface Props {
  x: number;
  y: number;
  w: number;
  h: number;
  /** Set when the editor is docked away from its cell: the cell it belongs to. */
  home?: string;
}

/**
 * The in-cell editor.
 *
 * The text lives in the store rather than here, because pointing at a cell to
 * insert its reference is a conversation between the grid and the editor — an
 * arrow key or a click on the sheet has to be able to rewrite the formula being
 * typed. This component owns the DOM selection and nothing else.
 */
export function CellEditor({ x, y, w, h, home }: Props): JSX.Element {
  useStore();
  const editing = store.editing;
  const text = editing?.text ?? '';
  const caret = editing?.caret ?? text.length;
  const pointing = editing?.pointing ?? null;

  const [picked, setPicked] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);

  // While pointing, the store rewrites the text under the caret — the DOM
  // selection has to follow or the next keystroke lands in the wrong place.
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el || el.selectionStart === caret) return;
    el.setSelectionRange(caret, caret);
  }, [caret, text]);

  const result = useMemo(() => suggestAt(text, caret, store.data()), [text, caret]);

  // Hide the list once what is typed IS the only remaining option — and never
  // while pointing, when the sheet, not the list, is what the user is reading.
  const settled =
    result.items.length === 1 &&
    result.items[0]!.label.toLowerCase() === result.typed.trim().toLowerCase();
  const showList = !dismissed && !pointing && result.items.length > 0 && !settled;

  useEffect(() => {
    setPicked(0);
  }, [result.start, result.typed, result.heading]);

  useEffect(() => {
    setDismissed(false);
  }, [result.heading]);

  /**
   * Everything the list offers, in the order the eye reads it.
   *
   * A function and each of its worked examples are separate CHOICES — the whole
   * point of showing `=GI.TOTAL("dwelling-1 bed-Count")` is that it is the thing
   * someone wants, and making them read it, dismiss the list and type it out is
   * the opposite of help. Arrowing walks the examples too, so the keyboard can
   * reach what the mouse can.
   */
  const options = useMemo(
    () =>
      result.items.flatMap((item) => [
        { item, example: undefined as string | undefined },
        ...(item.examples ?? []).map((example) => ({ item, example })),
      ]),
    [result.items]
  );

  const accept = (option: { item: Suggestion; example?: string }): void => {
    if (option.example === undefined) {
      const next = `${text.slice(0, result.start)}${option.item.insert}${text.slice(caret)}`;
      store.setEditText(next, result.start + option.item.caret);
      return;
    }
    /*
     * An example is a WHOLE formula, so it replaces the whole cell — unless the
     * call being typed is nested inside something else (`=SUM(1, gi.tot`), where
     * only its body belongs, and the leading `=` would be a syntax error.
     */
    const nested = result.start > 1;
    const next = nested
      ? `${text.slice(0, result.start)}${option.example.replace(/^=/, '')}${text.slice(caret)}`
      : option.example;
    store.setEditText(next, next.length);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (showList) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const n = options.length;
        setPicked((p) => (p + (e.key === 'ArrowDown' ? 1 : n - 1)) % n);
        return;
      }
      if (e.key === 'Tab' || e.key === 'Enter') {
        e.preventDefault();
        accept(options[picked] ?? options[0]!);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setDismissed(true);
        return;
      }
    }

    // Pointing: an arrow key inside a formula, at a spot a reference can go,
    // picks a cell on the sheet instead of moving the caret.
    if (
      (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') &&
      store.canPoint()
    ) {
      e.preventDefault();
      const [dr, dc] = {
        ArrowUp: [-1, 0],
        ArrowDown: [1, 0],
        ArrowLeft: [0, -1],
        ArrowRight: [0, 1],
      }[e.key] as [number, number];
      store.movePoint(dr, dc, e.shiftKey);
      return;
    }

    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        store.commitEdit(undefined, e.shiftKey ? 'none' : 'down');
        return;
      case 'Tab':
        e.preventDefault();
        store.commitEdit(undefined, 'right');
        return;
      case 'Escape':
        e.preventDefault();
        store.cancelEdit();
        return;
      case 'F4':
        // As in Excel: cycle $A$1 -> A$1 -> $A1 -> A1 on the reference the
        // caret sits on, rather than let the browser's own F4 (if any) act.
        e.preventDefault();
        store.toggleRefLock();
        return;
      default:
        break;
    }
    // Let arrows move the caret inside the text rather than the selection.
    e.stopPropagation();
  };

  const syncCaret = (): void => {
    const el = inputRef.current;
    if (el) store.setEditCaret(el.selectionStart ?? el.value.length);
  };

  /*
   * The formula's references, coloured to match their boxes on the grid.
   *
   * An <input> cannot colour part of its value, so the text is painted by a
   * mirror underneath and the input's own text is made transparent — the
   * standard trick, and the reason every metric in `.gt-editor-mirror` has to
   * match the input exactly. Only for formulas: a cell holding the word "A1"
   * is not a reference and must not be dressed as one.
   */
  const coloured = text.startsWith('=') ? colourRefs(text) : [];
  const pieces: JSX.Element[] = [];
  if (coloured.length > 0) {
    let at = 0;
    coloured.forEach((r, i) => {
      if (r.start > at) pieces.push(<span key={`t${i}`}>{text.slice(at, r.start)}</span>);
      pieces.push(
        <span key={`r${i}`} style={{ color: r.colour, fontWeight: 600 }}>
          {text.slice(r.start, r.end)}
        </span>
      );
      at = r.end;
    });
    if (at < text.length) pieces.push(<span key="tail">{text.slice(at)}</span>);
  }

  // The mirror does not scroll itself, so it follows the input's scroll.
  const [scrollLeft, setScrollLeft] = useState(0);

  return (
    <div className={`gt-editor${home ? ' is-away' : ''}`} style={{ left: x, top: y, minWidth: w, height: h }}>
      {home ? <span className="gt-editor-home" title="The cell this formula belongs to">{home}</span> : null}
      {pieces.length > 0 ? (
        <div className="gt-editor-mirror" aria-hidden="true">
          <span style={{ transform: `translateX(${-scrollLeft}px)` }}>{pieces}</span>
        </div>
      ) : null}
      <input
        ref={inputRef}
        className={pieces.length > 0 ? 'is-coloured' : undefined}
        value={text}
        spellCheck={false}
        onScroll={(e) => setScrollLeft(e.currentTarget.scrollLeft)}
        onChange={(e) => {
          store.setEditText(e.target.value, e.target.selectionStart ?? e.target.value.length);
          setDismissed(false);
        }}
        onKeyUp={syncCaret}
        onClick={syncCaret}
        onKeyDown={onKeyDown}
        // A pointing click lands on the grid, which blurs this input. Committing
        // on that blur would end the edit halfway through picking a reference.
        onBlur={() => {
          if (!store.editing?.pointing) store.commitEdit(undefined, 'none');
        }}
      />
      {showList ? (
        <Popover
          anchor={rectOf(inputRef.current)}
          onClose={() => setDismissed(true)}
          closeOnOutsidePress={false}
          className="gt-suggest"
        >
          {result.heading ? (
            <div className="gt-suggest-head">
              {result.heading}
              {result.note ? <div className="gt-suggest-note">{result.note}</div> : null}
            </div>
          ) : null}
          {result.items.map((item) => {
            const base = options.findIndex((o) => o.item === item && o.example === undefined);
            return (
              <div
                key={`${item.kind}:${item.label}`}
                className={`gt-suggest-row${base === picked ? ' is-picked' : ''}`}
                ref={base === picked ? scrollIntoView : undefined}
                onMouseDown={(e) => {
                  e.preventDefault();
                  accept({ item });
                }}
              >
                <span className="gt-suggest-name">
                  {item.label}
                  <span className={`gt-kind is-${item.kind}`}>{item.kind}</span>
                </span>
                <span className="gt-suggest-summary">{item.detail}</span>
                {/* Worked calls, in the monospace the formula bar uses — a
                    signature says the shape, these say what goes in it. Each is
                    a choice of its own: click it and it IS the formula. */}
                {item.examples?.map((example) => {
                  const at = options.findIndex((o) => o.item === item && o.example === example);
                  return (
                    <span
                      key={example}
                      className={`gt-suggest-example${at === picked ? ' is-picked' : ''}`}
                      title="Use this formula"
                      ref={at === picked ? scrollIntoView : undefined}
                      onMouseDown={(e) => {
                        // Stopped here, or the row underneath takes the click
                        // and inserts the bare function instead.
                        e.preventDefault();
                        e.stopPropagation();
                        accept({ item, example });
                      }}
                    >
                      {example}
                    </span>
                  );
                })}
              </div>
            );
          })}
        </Popover>
      ) : null}
    </div>
  );
}

/** Keep the highlighted row visible when arrowing through a long field list. */
function scrollIntoView(el: HTMLElement | null): void {
  el?.scrollIntoView({ block: 'nearest' });
}
